import { Code, ConnectError } from '@bufbuild/connect';
import { Empty } from '@bufbuild/protobuf';
import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Session } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { Sessions_RevokeReq } from '@/shared/api/protocol-ts/api/auth/svc_authentication_pb';
import { LoadingStateType } from '@/shared/config';
import { useAppDispatch, useMedia } from '@/shared/hooks';
import {
  Badge,
  Button,
  errorToastCaller,
  Spinner,
  toastCaller,
} from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { ModalID, modalModel } from '@/entities/modal';

import { i18n, SESSION_DATE_FORMAT } from '../../config';
import { parseUserAgent } from '../../lib';

import styles from './SessionList.module.scss';

type SessionListProps = {
  className?: string;
  // Without it the list shows the caller's own sessions.
  staff?: {
    userID: string;
    organizationID: string;
  };
  // Called when the caller's own sessions cannot be managed at all.
  onUnavailable?: () => void;
};

export const SessionList: FC<SessionListProps> = (props) => {
  const { className, staff, onUnavailable } = props;

  const { formatDate, formatMessage } = useIntl();
  const { isPhone } = useMedia();
  const dispatch = useAppDispatch();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingState, setLoadingState] = useState<LoadingStateType>('idle');
  const [isDenied, setIsDenied] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const owner = staff
    ? {
        case: 'Staff' as const,
        value: {
          UserID: staff.userID,
          OrganizationID: staff.organizationID,
        },
      }
    : undefined;

  const fetchSessions = async () => {
    setLoadingState('pending');

    try {
      const { Sessions, Total } = await dispatch(
        authModel.thunks.sessionsList({ Owner: owner }),
      ).unwrap();

      setSessions(Sessions);
      setTotal(Total);
      setLoadingState('succeeded');
    } catch (error) {
      // Own sessions: only a sign-in session may manage them. Staff: the
      // caller lacks the right or a sign-in session, or the target is a
      // Diagnocat admin; the code does not tell these apart.
      const isDeniedError =
        (error as ConnectError).code === Code.PermissionDenied;

      setIsDenied(isDeniedError);
      setLoadingState('failed');

      if (isDeniedError && !staff) {
        onUnavailable?.();
      }
    }
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff?.userID, staff?.organizationID]);

  const confirmRevoke = ({
    title,
    description,
    okText,
    successMessage,
    target,
    onRevoked,
  }: {
    title: string;
    description: string;
    okText: string;
    successMessage: string;
    target: Sessions_RevokeReq['Target'];
    onRevoked: () => void;
  }) => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title,
          description,
          okText,
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            setIsRevoking(true);

            try {
              await dispatch(
                authModel.thunks.sessionsRevoke({
                  Target: target,
                  Owner: owner,
                }),
              ).unwrap();
            } catch (error) {
              errorToastCaller(error as ConnectError);
              await fetchSessions();
              return;
            } finally {
              setIsRevoking(false);
            }

            onRevoked();
            toastCaller({ type: 'success', message: successMessage });
          },
        },
      }),
    );
  };

  const handleRevoke = (session: Session) =>
    confirmRevoke({
      title: formatMessage({
        id: 'activeSessions.revokeConfirmTitle',
        defaultMessage: 'End this session?',
      }),
      description: staff
        ? formatMessage({
            id: 'staffSessions.revokeConfirmDescription',
            defaultMessage:
              'The device or integration that uses it will be signed out. The staff member will get an email about it.',
          })
        : formatMessage({
            id: 'activeSessions.revokeConfirmDescription',
            defaultMessage:
              'The device or integration that uses it will be signed out.',
          }),
      okText: formatMessage({
        id: 'activeSessions.revoke',
        defaultMessage: 'End session',
      }),
      successMessage: formatMessage({
        id: 'activeSessions.revokeSuccess',
        defaultMessage: 'Session ended',
      }),
      target: { case: 'SessionID', value: session.ID },
      onRevoked: () => {
        setSessions((current) =>
          current.filter((item) => item.ID !== session.ID),
        );
        setTotal((current) => current - 1);
      },
    });

  const revokeAllLabel = staff
    ? formatMessage({
        id: 'staffSessions.revokeAll',
        defaultMessage: 'End all sessions',
      })
    : formatMessage({
        id: 'activeSessions.revokeAll',
        defaultMessage: 'End all other sessions',
      });

  const handleRevokeAll = () =>
    confirmRevoke({
      title: staff
        ? formatMessage({
            id: 'staffSessions.revokeAllConfirmTitle',
            defaultMessage: 'End all sessions?',
          })
        : formatMessage({
            id: 'activeSessions.revokeAllConfirmTitle',
            defaultMessage: 'End all other sessions?',
          }),
      description: staff
        ? formatMessage({
            id: 'staffSessions.revokeAllConfirmDescription',
            defaultMessage:
              'The staff member will be signed out on every device and integration, except sessions that work only in another clinic, and will get an email about it.',
          })
        : formatMessage({
            id: 'activeSessions.revokeAllConfirmDescription',
            defaultMessage:
              'Every device and integration except this one will be signed out.',
          }),
      okText: revokeAllLabel,
      successMessage: staff
        ? formatMessage({
            id: 'staffSessions.revokeAllSuccess',
            defaultMessage: 'All sessions ended',
          })
        : formatMessage({
            id: 'activeSessions.revokeAllSuccess',
            defaultMessage: 'All other sessions ended',
          }),
      target: { case: 'All', value: new Empty() },
      onRevoked: () => {
        // Own list: only the current session stays. Staff list: nothing
        // listed stays, sessions limited to other clinics are not listed.
        setSessions((current) => current.filter((item) => item.IsCurrent));
        setTotal(staff ? 0 : 1);
      },
    });

  const getDeviceLabel = (userAgent: string) => {
    const { browser, os } = parseUserAgent(userAgent);

    if (browser && os) {
      return formatMessage(
        {
          id: 'activeSessions.browserOnOS',
          defaultMessage: '{browser} on {os}',
        },
        { browser, os },
      );
    }

    return (
      browser ??
      os ??
      (userAgent ||
        formatMessage({
          id: 'activeSessions.unknownDevice',
          defaultMessage: 'Unknown device',
        }))
    );
  };

  const getDetails = (session: Session) => {
    const kindLabel = i18n.sessionKindLabels[session.Kind];

    return [
      kindLabel && formatMessage(kindLabel),
      session.IP &&
        formatMessage(
          { id: 'activeSessions.ip', defaultMessage: 'IP {ip}' },
          { ip: session.IP },
        ),
      session.LastActiveAt &&
        formatMessage(
          {
            id: 'activeSessions.lastActive',
            defaultMessage: 'Last active {date}',
          },
          {
            date: formatDate(
              session.LastActiveAt.toDate(),
              SESSION_DATE_FORMAT,
            ),
          },
        ),
      session.CreatedAt &&
        formatMessage(
          {
            id: 'activeSessions.startedAt',
            defaultMessage: 'Started {date}',
          },
          {
            date: formatDate(session.CreatedAt.toDate(), SESSION_DATE_FORMAT),
          },
        ),
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const titleClassName = isPhone || staff ? 'h5' : 'h4';
  const hiddenCount = total - sessions.length;
  const hasOtherSessions =
    hiddenCount > 0 || sessions.some((session) => !session.IsCurrent);

  const isFailed = loadingState === 'failed';

  return (
    <div
      className={cn(styles.container, className)}
      data-component="session-list"
    >
      <div className={styles.header}>
        <div className={styles.heading}>
          <h4 className={titleClassName}>
            <FormattedMessage
              id="activeSessions.title"
              defaultMessage="Active sessions"
            />
          </h4>

          {!isFailed && total > 0 && <Badge size="small">{total}</Badge>}
        </div>

        {!isFailed && hasOtherSessions && (
          <Button
            className={styles.action}
            variant="tertiary"
            size="medium"
            danger
            disabled={isRevoking}
            onClick={handleRevokeAll}
          >
            {revokeAllLabel}
          </Button>
        )}
      </div>

      {isFailed ? (
        <>
          <p className={cn(styles.hint, 'p3t')}>
            {isDenied ? (
              <FormattedMessage
                id="staffSessions.accessDenied"
                defaultMessage="You cannot manage the sessions of this staff member."
              />
            ) : (
              <FormattedMessage
                id="activeSessions.loadFailed"
                defaultMessage="Sessions could not be loaded."
              />
            )}
          </p>

          {!isDenied && (
            <Button
              className={styles.retry}
              variant="tertiary"
              size="medium"
              onClick={fetchSessions}
            >
              <FormattedMessage id="global.retry" defaultMessage="Try again" />
            </Button>
          )}
        </>
      ) : (
        <>
          <p className={cn(styles.hint, 'p3t')}>
            {staff ? (
              <FormattedMessage
                id="staffSessions.hint"
                defaultMessage="Devices and integrations signed in to this staff member’s account. When you end a session, the staff member gets an email about it."
              />
            ) : (
              <FormattedMessage
                id="activeSessions.hint"
                defaultMessage="Devices and integrations signed in to your account. If you do not recognize one, end it and change your password."
              />
            )}
          </p>

          {loadingState === 'pending' && sessions.length === 0 && (
            <Spinner className={styles.spinner} />
          )}

          {loadingState === 'succeeded' && total === 0 && (
            <p className={cn(styles.hint, 'p3t')}>
              <FormattedMessage
                id="staffSessions.empty"
                defaultMessage="This staff member has no active sessions."
              />
            </p>
          )}

          {sessions.length > 0 && (
            <ul className={styles.list}>
              {sessions.map((session) => (
                <li key={session.ID} className={styles.session}>
                  <div className={styles.info}>
                    <div className={styles.titleRow}>
                      <p className={cn(styles.device, 'p2')}>
                        {getDeviceLabel(session.UserAgent)}
                      </p>

                      {session.IsCurrent && (
                        <Badge size="small" tone="success">
                          <FormattedMessage
                            id="activeSessions.thisDevice"
                            defaultMessage="This device"
                          />
                        </Badge>
                      )}
                    </div>

                    <p className={cn(styles.details, 'p3')}>
                      {getDetails(session)}
                    </p>
                  </div>

                  {!session.IsCurrent && (
                    <Button
                      className={styles.action}
                      variant="tertiary"
                      size="small"
                      danger
                      disabled={isRevoking}
                      onClick={() => handleRevoke(session)}
                    >
                      <FormattedMessage
                        id="activeSessions.revokeShort"
                        defaultMessage="End"
                      />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {hiddenCount > 0 && (
            <p className={cn(styles.hint, 'p3t')}>
              <FormattedMessage
                id="activeSessions.hiddenCount"
                defaultMessage="{count, plural, one {# more session is not shown.} other {# more sessions are not shown.}}"
                values={{ count: hiddenCount }}
              />
            </p>
          )}
        </>
      )}
    </div>
  );
};
